import { Badge, Button, Center, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconMessages, IconThumbDown, IconThumbUp } from '@tabler/icons-react';
import { ANSWER_TYPES, formatListDate, shopperLabel } from './conversationFormat';
import classes from './Conversations.module.css';

export const AnswerTypeBadge = ({ type, size = 'sm' }) => {
  const meta = ANSWER_TYPES[type];
  if (!meta) return null;
  return (
    <Badge variant="light" color={meta.color} size={size}>
      {meta.label}
    </Badge>
  );
};

// Up and down vote counts of a conversation; nothing when it has none.
const VoteCounts = ({ feedback }) => {
  const counts = [
    { key: 'up', Icon: IconThumbUp, label: 'thumbs up', count: feedback?.up || 0 },
    { key: 'down', Icon: IconThumbDown, label: 'thumbs down', count: feedback?.down || 0 },
  ].filter(({ count }) => count > 0);
  return counts.map(({ key, Icon, label, count }) => (
    <Group key={key} gap={2} wrap="nowrap" aria-label={`${count} ${label}`}>
      <Icon size={12} color="var(--mantine-color-dimmed)" />
      <Text size="xs" c="dimmed">
        {count}
      </Text>
    </Group>
  ));
};

const ConversationListItem = ({ session, selected, onOpen }) => (
  <UnstyledButton
    className={classes.item}
    data-selected={selected || undefined}
    aria-current={selected ? 'true' : undefined}
    onClick={() => onOpen(session.chatSessionId)}
  >
    <Text size="sm" fw={600} lineClamp={1}>
      {shopperLabel(session.shopper)}
    </Text>
    {session.firstQuestion && (
      <Text size="xs" c="dimmed" lineClamp={1}>
        {session.firstQuestion}
      </Text>
    )}
    <Group justify="space-between" gap={4} mt={4} wrap="nowrap">
      <Text size="xs" c="dimmed">
        {formatListDate(session.lastMessageAt)}
      </Text>
      <Group gap={6} wrap="nowrap">
        {session.answerTypes?.includes('unanswered') && <AnswerTypeBadge type="unanswered" size="xs" />}
        <VoteCounts feedback={session.feedback} />
        <Text size="xs" c="dimmed">
          {session.messageCount} msg
        </Text>
      </Group>
    </Group>
  </UnstyledButton>
);

const ConversationList = ({ sessions, total, selectedId, onOpen, filtered, hasMore, onLoadMore, loadingMore }) => {
  if (sessions.length === 0) {
    return (
      <Center py={60} px="md">
        <Stack align="center" gap="xs" ta="center">
          <IconMessages size={40} stroke={1} color="var(--mantine-color-gray-5)" />
          <Text fw={500}>No conversations</Text>
          <Text size="sm" c="dimmed">
            {filtered
              ? 'No conversation in this period matches the search or filter.'
              : 'Shoppers have not chatted with the sales agent in this period.'}
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <>
      {sessions.map((session) => (
        <ConversationListItem
          key={session.chatSessionId}
          session={session}
          selected={session.chatSessionId === selectedId}
          onOpen={onOpen}
        />
      ))}
      <Stack align="center" gap={6} py="sm">
        <Text size="xs" c="dimmed">
          Showing {sessions.length} of {total} conversations
        </Text>
        {hasMore && (
          <Button size="xs" variant="light" onClick={onLoadMore} loading={loadingMore}>
            Load more
          </Button>
        )}
      </Stack>
    </>
  );
};

export default ConversationList;
